import { Button, EmptyState, Spinner } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useShows } from "../../app/hooks/useShows.js";
import { ApiError } from "../../app/hooks/apiError.js";
import styles from "./UpcomingCard.module.scss";

type ShowSummary = components["schemas"]["ShowSummary"];

/** Device-local `YYYY-MM-DD` for "today" — the upcoming window starts now, in the viewer's timezone.*/
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Zoneless domain date: parse `YYYY-MM-DD` at local noon (never UTC), then format the day-of-week + day. */
function parseDomainDate(iso: string): Date {
  return new Date(`${iso}T12:00:00`);
}

const KIND_LABELS: Record<string, string> = {
  show: "Show",
  rehearsal: "Rehearsal",
  festival: "Festival",
  promo: "Promo",
};
const kindLabel = (kind: string | null | undefined) =>
  kind ? (KIND_LABELS[kind] ?? kind.charAt(0).toUpperCase() + kind.slice(1)) : "Show";

/**
 * The dashboard "Upcoming" card (cd-dashboard `.upcoming`) — the next confirmed shows/rehearsals ahead,
 * device-local, from the money-free shows read (`useShows` is money-free by construction). Repaints live
 * on `schedule-changed` via the shared `['band', bandId, 'shows']` key. Born-blank → designed empty state; never a
 * fabricated row.
 */
export function UpcomingCard() {
  const q = useShows({ status: "confirmed", sort: "date", from: todayIso() });
  const rows = (q.data?.items ?? []).slice(0, 5);

  return (
    <section className={styles.box} aria-labelledby="upcoming-heading">
      <header className={styles.head}>
        <h3 id="upcoming-heading" className={styles.title}>
          Upcoming
        </h3>
        {!q.isLoading && !q.isError && rows.length > 0 && <span className={styles.tag}>{rows.length}</span>}
      </header>

      {q.isLoading ? (
        <div className={styles.center} role="status" aria-live="polite">
          <Spinner />
        </div>
      ) : q.isError ? (
        <EmptyState
          title="Couldn't load your schedule"
          description={q.error instanceof ApiError ? q.error.message : "Please try again."}
          action={<Button onClick={() => void q.refetch()}>Retry</Button>}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing upcoming yet."
          description="Confirmed shows and rehearsals ahead show up here."
        />
      ) : (
        <ul className={styles.list}>
          {rows.map((row) => (
            <UpcomingRow key={row.showId} row={row} />
          ))}
        </ul>
      )}
    </section>
  );
}

function UpcomingRow({ row }: { row: ShowSummary }) {
  const d = row.date ? parseDomainDate(row.date) : null;
  const dow = d ? d.toLocaleDateString(undefined, { weekday: "short" }) : "—";
  const day = d ? String(d.getDate()).padStart(2, "0") : "—";
  const kind = (row.kind ?? "show").toLowerCase();
  const isShow = kind === "show" || kind === "festival";
  const cityLine = row.city ? `${row.city}${row.state ? `, ${row.state}` : ""}` : null;
  const title = cityLine ?? row.venueName ?? kindLabel(kind);
  const sub = cityLine ? row.venueName : null;

  return (
    <li className={styles.row}>
      <div className={styles.date}>
        <span className={styles.dow}>{dow}</span>
        <span className={styles.day}>{day}</span>
      </div>
      <div className={styles.main}>
        <p className={styles.rowTitle}>{title}</p>
        {sub && <p className={styles.rowSub}>{sub}</p>}
      </div>
      <span className={styles.kind} data-kind={isShow ? "show" : "other"}>
        {kindLabel(kind)}
      </span>
    </li>
  );
}
